import { useRef, useEffect, useState, useCallback, createContext, useContext, MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames/bind';
import { getContainerPortalId } from '../../utils';

import { ToastProviderPropType, Toast } from './types/propsType';
import styles from './ToastProvider.module.scss';

const cx = classNames.bind(styles);

const ToastContext = createContext<{
  addToast: (_toast: Toast, _autoClose?: boolean) => void;
  deleteToast: (_index: string, _closeToast?: () => boolean) => void;
}>({
  addToast: (_toast: Toast, _autoClose?: boolean) => null,
  deleteToast: (_index: string, _closeToast?: () => boolean) => null,
});

const ToastProvider = ({ children, portalContainerId }: ToastProviderPropType): JSX.Element => {
  const [toasts, setToasts] = useState<Array<Toast>>([]);
  const notificationRef = useRef() as MutableRefObject<HTMLDivElement>;
  const notificationItemRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [slideOutDelay, setSlideOutDelay] = useState(false);
  const [slideOut, setSlideOut] = useState(false);

  useEffect(() => {
    const domDoc = document?.getElementById('nextgen-drawer-component');
    if (domDoc) {
      const notificationComponent = notificationRef.current;
      notificationComponent.style.bottom = `${domDoc.offsetHeight + 16}px`;
    }
  }, []);
  // Interim Code Revert
  // useEffect(() => {
  //   const domHeaderDoc = document?.getElementById(navHeaderId);
  //   const notificationItemComponent = notificationItemRef.current;
  //   if (domHeaderDoc && notificationItemComponent) {
  //     const { bottom } = domHeaderDoc.getBoundingClientRect();
  //     notificationItemComponent.style.top = `${bottom}px`;
  //   }
  // }, [toasts]);
  const addToast = useCallback(
    function (toast: Toast, autoClose = true) {
      setToasts((toasts) => [...toasts, toast]);
      setSlideOut(false);
      if (autoClose) {
        setSlideOutDelay(true);
        setTimeout(() => {
          setToasts((toasts) => toasts.slice(1));
        }, 3100);
      }
    },
    [setToasts],
  );

  const deleteToast = useCallback(
    function (id: string) {
      setToasts((toasts) => {
        const prevToasts = toasts;
        const index = prevToasts.findIndex((toast) => toast.id === id);
        if (index > -1) {
          prevToasts[index].closeNotification = true;

          setTimeout(() => {
            setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
          }, 300);
        }
        return [...prevToasts];
      });
    },
    [setToasts],
  );
  const portalContainer = portalContainerId && getContainerPortalId(portalContainerId);

  const toastBody = (
    <div className={styles['notifications-container']} ref={notificationRef} data-testid="toast-result-testid">
      {toasts.map((toast) => {
        const CClassname = cx('toast-notification-item', {
          'toast-auto-close-notification-wrapper': slideOutDelay,
          'toast-slide-in-notification-wrapper': !slideOutDelay,
          'toast-slide-out-notification-wrapper': toast.closeNotification || slideOut,
        });
        return (
          <div key={toast.id} className={CClassname} ref={notificationItemRef}>
            {toast.content}
          </div>
        );
      })}
    </div>
  );
  return (
    <ToastContext.Provider value={{ addToast, deleteToast }}>
      <>{children}</>
      {portalContainer ? createPortal(toastBody, portalContainer) : toastBody}
    </ToastContext.Provider>
  );
};

const useToast = () => {
  const { addToast, deleteToast } = useContext<{
    addToast: (_toast: Toast, _autoClose?: boolean) => void;
    deleteToast: (_index: string, _closeToast?: () => boolean) => void;
  }>(ToastContext);
  return { addToast, deleteToast };
};

export { useToast };
export default ToastProvider;
